import { useEffect, useRef, useState } from "react";
import { api, fmtDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Loader2, Eraser, CheckCircle2 } from "lucide-react";

export function SignaturePad({ open, onOpenChange, onConfirm, busy, customerName }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [empty, setEmpty] = useState(true);

  const setup = () => {
    const c = canvasRef.current;
    if (!c) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const rect = c.getBoundingClientRect();
    c.width = Math.round(rect.width * dpr);
    c.height = Math.round(rect.height * dpr);
    const ctx = c.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, rect.width, rect.height);
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#0B110F";
    setEmpty(true);
  };

  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(setup, 80);
    const html = document.documentElement;
    const prev = html.style.overscrollBehavior;
    html.style.overscrollBehavior = "none";
    // Non-passive touch handlers so iOS never turns a stroke into scroll / pull-to-refresh.
    const stop = (e) => { if (e.target === canvasRef.current) e.preventDefault(); };
    document.addEventListener("touchstart", stop, { passive: false });
    document.addEventListener("touchmove", stop, { passive: false });
    return () => {
      clearTimeout(t);
      html.style.overscrollBehavior = prev;
      document.removeEventListener("touchstart", stop);
      document.removeEventListener("touchmove", stop);
    };
  }, [open]);

  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const down = (e) => {
    e.preventDefault();
    canvasRef.current.setPointerCapture?.(e.pointerId);
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + 0.1, y + 0.1);
    ctx.stroke();
    drawing.current = true;
    setEmpty(false);
  };

  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const up = () => { drawing.current = false; };

  const confirm = () => {
    if (empty) return;
    onConfirm(canvasRef.current.toDataURL("image/png"));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="signature-dialog" className="bg-zinc-950 border border-zinc-800 text-zinc-100 max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display uppercase">Assine para aprovar</DialogTitle>
          <DialogDescription className="text-zinc-400">
            {customerName ? `${customerName}, assine` : "Assine"} com o dedo no quadro abaixo.
          </DialogDescription>
        </DialogHeader>
        <canvas
          ref={canvasRef}
          data-testid="signature-canvas"
          onPointerDown={down}
          onPointerMove={move}
          onPointerUp={up}
          onPointerLeave={up}
          onPointerCancel={up}
          className="w-full h-48 rounded-xl border-2 border-dashed border-green-brand/50 bg-white cursor-crosshair"
          style={{ touchAction: "none", overscrollBehavior: "none", WebkitUserSelect: "none", userSelect: "none" }}
        />
        <div className="grid grid-cols-2 gap-3">
          <Button
            data-testid="signature-clear-btn"
            type="button"
            variant="outline"
            onClick={setup}
            disabled={busy}
            className="h-11 bg-zinc-900 border-zinc-800 text-zinc-200 hover:bg-zinc-800 font-bold uppercase"
          >
            <Eraser className="h-4 w-4 mr-2" /> Limpar
          </Button>
          <Button
            data-testid="signature-confirm-btn"
            type="button"
            onClick={confirm}
            disabled={busy || empty}
            className="h-11 bg-green-brand hover:bg-green-brandDark font-bold uppercase"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><CheckCircle2 className="h-4 w-4 mr-2" /> Confirmar</>)}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SignatureImage({ orderId, sigId }) {
  const [src, setSrc] = useState("");
  useEffect(() => {
    let url = "";
    (async () => {
      try {
        const resp = await api.get(`/orders/${orderId}/signatures/${sigId}`, { responseType: "blob" });
        url = URL.createObjectURL(resp.data);
        setSrc(url);
      } catch {}
    })();
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [orderId, sigId]);
  if (!src) return <div className="h-20 w-48 rounded-lg bg-zinc-900 animate-pulse" />;
  return <img data-testid={`signature-img-${sigId}`} src={src} alt="Assinatura do cliente" className="h-20 max-w-[240px] rounded-lg bg-white object-contain p-1" />;
}

export function ApprovalInfo({ orderId, approval, testId = "budget-approval" }) {
  if (!approval) return null;
  return (
    <div data-testid={testId} className="mt-4 rounded-xl border border-green-brand/40 bg-green-brand/5 p-3 space-y-2">
      <div className="text-sm text-zinc-100">
        Aprovado por <span className="font-bold">{approval.name || "cliente"}</span> em {fmtDateTime(approval.signed_at)}
      </div>
      {approval.signature_id && <SignatureImage orderId={orderId} sigId={approval.signature_id} />}
    </div>
  );
}
